import React from 'react';
import { Button, SearchBox, Tag, TagGroup } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { GuideSegmented } from '../../../../theme/components/GuideSegmented';
import { ReviewView, WORK_STATES, WorkStateCounts, WorkStateFilter } from '../ContractReview.Functions';

/*
 * Barra de la lista de trabajo: qué estado ver (por decidir / aceptados / rechazados / aplicados / todos), tipo
 * (cambios / avisos), búsqueda, el chip del concepto cuando se llegó desde "Concepto por concepto" y marcar los visibles.
 */

export interface ContractReviewWorkToolbarProps {
  counts: WorkStateCounts;
  state: WorkStateFilter;
  onStateChange: (state: WorkStateFilter) => void;
  view: ReviewView;
  onViewChange: (view: ReviewView) => void;
  changesCount: number;
  observationsCount: number;
  text: string;
  onTextChange: (text: string) => void;
  shown: number;
  total: number;
  conceptLabel?: string;
  onRemoveConcept?: () => void;
  /** Cuántos de los visibles se pueden marcar; sin valor no se ofrece marcar. */
  selectableCount?: number;
  selectedCount?: number;
  onSelectAll?: () => void;
  onClearSelection?: () => void;
}

export const ContractReviewWorkToolbar: React.FC<ContractReviewWorkToolbarProps> = props => {
  const { t } = useTranslation();
  const hint = t(`contractReview.work.stateHint.${props.state}`);

  return (
    <div className="crv-toolbar" data-testid="review-toolbar">
      <div className="crv-toolbar__row">
        <GuideSegmented<WorkStateFilter>
          label={t('contractReview.work.stateLabel')}
          ariaLabel={t('contractReview.work.stateLabel')}
          options={[...WORK_STATES, 'all' as const].map(state => ({ value: state, label: t(`contractReview.work.state.${state}`, { count: props.counts[state] }) }))}
          value={props.state}
          onChange={props.onStateChange}
          data-testid="review-state"
        />
        <SearchBox
          className="crv-toolbar__search"
          aria-label={t('contractReview.work.search')}
          placeholder={t('contractReview.work.search')}
          value={props.text}
          onChange={(_, data) => props.onTextChange(data.value)}
        />
      </div>
      <div className="crv-toolbar__row crv-toolbar__row--secondary">
        <GuideSegmented<ReviewView>
          label={t('contractReview.work.typeLabel')}
          ariaLabel={t('contractReview.work.typeLabel')}
          options={[
            { value: 'all', label: t('contractReview.work.type.all') },
            { value: 'changes', label: t('contractReview.work.type.changes', { count: props.changesCount }) },
            { value: 'observations', label: t('contractReview.work.type.observations', { count: props.observationsCount }) }
          ]}
          value={props.view}
          onChange={props.onViewChange}
          data-testid="review-view"
        />
        {props.conceptLabel && props.onRemoveConcept && (
          <TagGroup aria-label={t('contractReview.work.conceptFilter', { label: props.conceptLabel })} onDismiss={props.onRemoveConcept}>
            <Tag dismissible value="concept" dismissIcon={{ 'aria-label': t('contractReview.work.removeConcept') }} data-testid="review-concept-chip">
              {t('contractReview.work.conceptFilter', { label: props.conceptLabel })}
            </Tag>
          </TagGroup>
        )}
        <span className="crv-toolbar__count" aria-live="polite">
          {t('contractReview.work.count', { shown: props.shown, total: props.total })}
        </span>
        {props.selectableCount !== undefined && props.selectableCount > 0 && (
          <span className="crv-toolbar__select">
            {(props.selectedCount ?? 0) < props.selectableCount && (
              <Button appearance="transparent" size="small" onClick={props.onSelectAll} data-testid="review-select-all">
                {t('contractReview.work.selectAll', { count: props.selectableCount })}
              </Button>
            )}
            {(props.selectedCount ?? 0) > 0 && (
              <Button appearance="transparent" size="small" onClick={props.onClearSelection} data-testid="review-clear-selection">
                {t('contractReview.work.clearSelection')}
              </Button>
            )}
          </span>
        )}
      </div>
      {hint && (
        <p className="crv-toolbar__hint" data-testid="review-state-hint">
          {hint}
        </p>
      )}
    </div>
  );
};

export default ContractReviewWorkToolbar;
